<template>
    <div
        :class="{
            'navbar-layout-container': display.mdAndUp,
            'navbar-layout-container mt-10': display.smAndDown,
        }"
    >
        <!-- Esenda logo -->
        <div class="navbar-logo-container">
            <v-img
                class="navbar-logo-esenda-img"
                :src="getImageUrl('esenda.png')"
                alt="Esenda logo"
            />
        </div>

        <!-- Menu list -->
        <v-list color="transparent" class="navbar-menu-items-container">
            <v-list-item v-for="(item, i) in filteredRoutes" :key="i">
                <div
                    :class="{
                        'navbar-menu-item-active': isParentItemActive(item),
                        'navbar-menu-item': !isParentItemActive(item),
                    }"
                    @click="
                        item.subitems
                            ? toggleSubItems(i)
                            : handleClick(item.route, item.title)
                    "
                >
                    <v-img
                        :class="{
                            'navbar-item-icon-active': isParentItemActive(item),
                            'navbar-item-icon': !isParentItemActive(item),
                        }"
                        :src="item.iconPath"
                    />
                    <span class="navbar-menu-item-text h200">{{
                        item.title
                    }}</span>
                    <img
                        v-if="item.subitems"
                        :class="{
                            'navbar-item-icon-arrow-expanded':
                                isSubmenuExpanded && expandedIndex === i,
                            'navbar-item-icon-arrow-collapsed':
                                !isSubmenuExpanded || expandedIndex !== i,
                        }"
                        :src="getImageUrl('icons/12x12/ic12-arrow-up.svg')"
                    />
                </div>
                <!-- Subitems -->
                <v-list
                    v-if="
                        item.subitems &&
                        isSubmenuExpanded &&
                        expandedIndex === i
                    "
                    color="transparent"
                >
                    <v-list-item v-for="(subitem, j) in item.subitems" :key="j">
                        <div
                            :class="{
                                'navbar-submenu-item-active':
                                    isSubItemActive(subitem),
                                'navbar-submenu-item':
                                    !isSubItemActive(subitem),
                            }"
                            @click="handleClick(subitem.route, item.title)"
                        >
                            <v-img
                                :src="subitem.iconPath"
                                :class="{
                                    'navbar-submenu-item-icon-active':
                                        isSubItemActive(subitem),
                                    'navbar-submenu-item-icon':
                                        !isSubItemActive(subitem),
                                }"
                            />
                            <span class="navbar-menu-item-text body-small">{{
                                subitem.title
                            }}</span>
                        </div>
                    </v-list-item>
                </v-list>
            </v-list-item>
        </v-list>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { usePiniaStore } from "@/stores/store";
import { useSessionStore } from "@/stores/session";
import { signOut } from "aws-amplify/auth";

import {
    navPartnerRoutes,
    noPermissionsPartnerRoutes,
    checkFirstTwoRouteSegmentsMatch,
} from "@/methods/navbarRoutes";
import { getImageUrl } from "@/methods/iconHelpers";

import type { Route } from "@/types/routes";
import { redirectToRoute } from "@/router";
import { hasRequiredPermissions } from "@/methods/permissionsHelpers";
import { Permissions } from "@/constants/access";
import { AuthRouteNames } from "@/constants/routes";

const piniaStore = usePiniaStore();
const sessionStore = useSessionStore();
const router = useRouter();
const route = useRoute();
const display = ref(useDisplay());

const partnerRoutes: Route[] = navPartnerRoutes;
const noPermissionsRoutes: Route[] = noPermissionsPartnerRoutes;
const expandedIndex = ref<number | null>(null);
const isSubmenuExpanded = ref(false);

const hasPermissions = (requiredPermissions) => {
    /** Check if the user has all required permissions */
    return hasRequiredPermissions(requiredPermissions);
};

const filteredRoutes = computed(() => {
    let filtered =
        sessionStore.isAuthenticated && hasPermissions([Permissions.BASE])
            ? partnerRoutes
            : noPermissionsRoutes;

    return filtered;
});

const path = computed(() => route.path);

const handleClick = (path: string, _title: string) => {
    if (path === "logout") {
        handleLogout();
    } else {
        changeRoute(path);
    }
};

const changeRoute = (path: string) => {
    router.push(path).catch((err) => {
        console.log("Route error: " + err);
    });
};

const toggleSubItems = (index: number) => {
    expandedIndex.value = expandedIndex.value === index ? null : index;
    isSubmenuExpanded.value = expandedIndex.value !== null;
};

const isParentItemActive = (item) => {
    /**
     * Determines if a parent navigation item is currently active based on the current path.
     * If the item has multiple path patterns (`multiplePaths`), it checks if any of the regex
     * patterns in `item.matchPath` match the current path.
     * Otherwise, it checks if the first two segments of the item's route and the current path match.
     *
     * @param {Object} item the navigation item to check
     * @returns {boolean} `true` if the item is considered active, otherwise `false`.
     */
    if (item.multiplePaths) {
        return item.matchPath.some((pattern) => pattern.test(path.value));
    }
    return checkFirstTwoRouteSegmentsMatch(item.route, path.value);
};

const isSubItemActive = (subitem) =>
    subitem.multiplePaths
        ? subitem.matchPath.some((pattern) => pattern.test(path.value))
        : path.value === subitem.route;

const handleLogout = async () => {
    await signOut({ global: true });
    piniaStore.clearUserSession();
    redirectToRoute({ name: AuthRouteNames.LOGIN });
};

// Ensure submenu is expanded when component mounts if the current route is a subitem route
onMounted(async () => {
    setTimeout(() => {
        filteredRoutes.value.forEach((item, index) => {
            if (
                item.subitems &&
                item.subitems.some((subitem) => subitem.route === path.value)
            ) {
                expandedIndex.value = index;
                isSubmenuExpanded.value = true;
            }
        });
    }, 1000);
});

watch(path, () => {
    // Close submenu if the current path doesn't match any of the submenu routes
    if (
        expandedIndex.value != null &&
        filteredRoutes.value[expandedIndex.value] &&
        !isParentItemActive(filteredRoutes.value[expandedIndex.value])
    ) {
        expandedIndex.value = null;
    }
});
</script>

<style lang="css" scoped>
.v-list {
    padding: 0 !important;
}
</style>
